// src/utils/vitalsTrend.js
//
// Pure logic for the patient record's vitals: series for the trend charts,
// the "+12/+7 since Aug" delta, the BP reference band, whether a trend can be
// drawn at all, how stale the latest reading is, and who recorded it.
// Readings are the contract's VitalsReading (docs/visit-workflow-contract.md).

import { localDay, localTime } from './consultVitals';
import { addDays, toDay, fmtDayShort, fmtDayFull } from './activeMedicines';

export const STALE_AFTER_DAYS = 30;
export const MAX_CHART_POINTS = 12;
export const BP_BAND = { systolic: 140, diastolic: 90 };

const stamp = r => { const d = new Date(r?.measuredAt); return Number.isNaN(d.getTime()) ? 0 : d.getTime(); };

/** Newest first, whatever order the API used. Does not mutate. */
export const newestFirst = readings => (Array.isArray(readings) ? readings : [])
  .filter(r => r && r.measuredAt).slice().sort((a, b) => stamp(b) - stamp(a));

/**
 * Chart points for one measurement, oldest → newest (last MAX_CHART_POINTS).
 * `key` is a reading field ('pulse', 'weightKg', …); readings without it are skipped.
 * @returns {{t: number, day: string, measuredAt: string, value: number, reading: object}[]}
 */
export function seriesFor(readings, key, limit = MAX_CHART_POINTS) {
  return newestFirst(readings)
    .filter(r => typeof r[key] === 'number')
    .slice(0, limit).reverse()
    .map(r => ({ t: stamp(r), day: localDay(r.measuredAt), measuredAt: r.measuredAt, value: r[key], reading: r }));
}

/** BP points need both numbers: {systolic, diastolic} per reading. */
export function bpSeries(readings, limit = MAX_CHART_POINTS) {
  return newestFirst(readings)
    .filter(r => typeof r.systolic === 'number' && typeof r.diastolic === 'number')
    .slice(0, limit).reverse()
    .map(r => ({ t: stamp(r), day: localDay(r.measuredAt), measuredAt: r.measuredAt, systolic: r.systolic, diastolic: r.diastolic, reading: r }));
}

/** A line needs two points; one reading is shown as a single dot with a note. */
export const trendAvailable = series => Array.isArray(series) && series.length >= 2;

export const bpAboveBand = p => p.systolic >= BP_BAND.systolic || p.diastolic >= BP_BAND.diastolic;

/** Change of the latest point from the one before it; null with fewer than two points. */
export function deltaSincePrevious(series, pick = p => p.value) {
  if (!trendAvailable(series)) return null;
  const last = series[series.length - 1];
  const prev = series[series.length - 2];
  return { change: pick(last) - pick(prev), since: prev.day, sinceLabel: fmtDayShort(prev.day) };
}

/** BP delta: {systolic, diastolic, since, sinceLabel} or null. */
export function bpDelta(series) {
  if (!trendAvailable(series)) return null;
  const last = series[series.length - 1];
  const prev = series[series.length - 2];
  return {
    systolic: last.systolic - prev.systolic, diastolic: last.diastolic - prev.diastolic,
    since: prev.day, sinceLabel: fmtDayShort(prev.day),
  };
}

export const signed = n => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');
/** Rounded to 1 dp without a trailing ".0". */
export const round1 = n => Math.round(n * 10) / 10;

/**
 * Y scale for a chart: pads the data range, and can be forced to include
 * extra values (the BP band edge). Returns a function mapping value → 0..1 (1 = top).
 */
export function chartScale(values, { include = [], padRatio = 0.12, minSpan = 1 } = {}) {
  const all = [...values, ...include].filter(v => typeof v === 'number');
  if (!all.length) return { min: 0, max: 1, at: () => 0.5 };
  let lo = Math.min(...all);
  let hi = Math.max(...all);
  if (hi - lo < minSpan) { const mid = (hi + lo) / 2; lo = mid - minSpan / 2; hi = mid + minSpan / 2; }
  const pad = (hi - lo) * padRatio;
  const min = lo - pad;
  const max = hi + pad;
  return { min, max, at: v => (v - min) / (max - min) };
}

/** True once the reading is more than `days` old (a reading exactly 30 days old is still current). */
export function isStale(measuredAt, today = new Date(), days = STALE_AFTER_DAYS) {
  const day = localDay(measuredAt);
  if (!day) return false;
  return addDays(day, days) < toDay(today);
}

/**
 * Heading of the Latest vitals tile.
 * @returns {{kind: 'none'|'recent'|'stale'|'unknown', label: string}}
 *   recent → "Latest vitals · 30 Oct, 10:12"   stale → "Last recorded · 12 Sep 2026"
 */
export function latestVitalsHeading(reading, today = new Date()) {
  if (!reading || !reading.measuredAt) return { kind: 'none', label: 'Latest vitals' };
  const day = localDay(reading.measuredAt);
  if (!day) return { kind: 'unknown', label: 'Latest vitals' };
  if (isStale(reading.measuredAt, today)) return { kind: 'stale', label: `Last recorded · ${fmtDayFull(day)}` };
  const t = localTime(reading.measuredAt);
  return { kind: 'recent', label: `Latest vitals · ${fmtDayShort(day)}${t ? `, ${t}` : ''}` };
}

const SELF_SOURCES = ['PATIENT', 'SELF', 'SELF_REPORTED', 'PATIENT_APP', 'APP'];

/** "Recorded at the clinic" / "Recorded by navigator" / "Self-reported" ('' when the source is unknown). */
export function sourcePhrase(source) {
  const s = String(source || '').toUpperCase();
  if (s === 'CLINIC') return 'Recorded at the clinic';
  if (s === 'NAVIGATOR') return 'Recorded by navigator';
  if (SELF_SOURCES.includes(s)) return 'Self-reported';
  return '';
}

/** Short "By" cell: "Clinic · Nina N." */
export function sourceShort(source, recordedByName) {
  const s = String(source || '').toUpperCase();
  const base = s === 'CLINIC' ? 'Clinic' : s === 'NAVIGATOR' ? 'Navigator' : SELF_SOURCES.includes(s) ? 'Self-reported' : '';
  return [base, recordedByName].filter(Boolean).join(' · ') || '—';
}
